import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { Target, Flame, ChevronRight, Trophy, MessageSquare, BookOpen, CheckCircle2 } from 'lucide-react';

const challengeIcons = {
  messages: MessageSquare,
  victories: Trophy,
  modules: BookOpen,
  default: Target,
};

const ChallengesCard = () => {
  const [challenges, setChallenges] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { api } = useAuth();

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const [challengesRes, streakRes] = await Promise.all([
        api.get('/challenges'),
        api.get('/streak'),
      ]);
      // Prendre seulement les 3 premiers défis hebdomadaires non complétés
      const weekly = challengesRes.data
        .filter(c => c.period === 'weekly' && !c.is_completed)
        .slice(0, 3);
      setChallenges(weekly);
      setStreak(streakRes.data);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardContent className="p-6 flex items-center justify-center">
          <Target className="w-6 h-6 animate-pulse text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const completedCount = challenges.filter(c => c.is_completed).length;

  return (
    <Card className="bg-card/50 backdrop-blur border-border/50 overflow-hidden" data-testid="challenges-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-orange-500" />
            Mes Défis
          </CardTitle>
          <div className="flex items-center gap-2">
            {streak.current_streak > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-orange-500/20 rounded-full">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-500">{streak.current_streak}j</span>
              </div>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/challenges')}
              className="text-muted-foreground hover:text-foreground"
            >
              Voir tout
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {challenges.length === 0 ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Tous les défis sont complétés !</p>
            <p className="text-xs text-muted-foreground mt-1">Reviens la semaine prochaine</p>
          </div>
        ) : (
          challenges.map((challenge) => {
            const IconComponent = challengeIcons[challenge.challenge_type] || challengeIcons.default;
            const currentCount = challenge.current_count ?? 0;
            const progress = Math.min(100, (currentCount / challenge.target_count) * 100);
            const pending = challenge.claim_status === 'pending_coach' || challenge.claim_status === 'pending_ai';
            
            return (
              <div 
                key={challenge.id} 
                className="p-3 rounded-lg bg-background/50 border border-border/50 hover:border-orange-500/30 transition-colors"
                data-testid={`dashboard-challenge-${challenge.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-4 h-4 text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-medium truncate">{challenge.title}</h4>
                      <span className="text-xs text-muted-foreground ml-2">
                        {currentCount}/{challenge.target_count}
                      </span>
                    </div>
                    <Progress value={progress} className="h-1.5" />
                    <div className="flex items-center justify-between mt-1 gap-2">
                      <span className="text-xs text-muted-foreground truncate">{challenge.description}</span>
                      <span className="text-xs font-medium text-orange-500 flex-shrink-0">+{challenge.xp_reward} XP</span>
                    </div>
                    {challenge.can_declare && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 h-7 text-xs w-full"
                        onClick={() => navigate('/challenges')}
                        data-testid={`dashboard-declare-${challenge.id}`}
                      >
                        Valider ce défi
                      </Button>
                    )}
                    {pending && (
                      <p className="text-xs text-muted-foreground mt-1">Vérification en cours</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};

export default ChallengesCard;
